import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { api } from '../../../lib/api'
import type { Content, ContentDetail, ContentStatus } from '../types'

type UseContentsOptions = {
  /** Filtrer par collection (null = tous, 'orphan' = sans collection) */
  collectionId?: number | 'orphan' | null
  /** Filtrer par statut */
  status?: ContentStatus | 'all'
  /** Activer le polling pour les contenus en traitement */
  enablePolling?: boolean
  /** Intervalle de polling en ms */
  pollingInterval?: number
}

type UseContentsReturn = {
  contents: Content[]
  /** Tous les contenus (non filtrés) pour les compteurs */
  allContents: Content[]
  loading: boolean
  error: string | null
  
  // Actions
  reload: () => Promise<void>
  remove: (id: number) => Promise<boolean>
  reindex: (id: number, strategy?: string) => Promise<boolean>
  forceTranscribe: (id: number) => Promise<boolean>
  getDetail: (id: number) => Promise<ContentDetail | null>
  downloadTranscription: (id: number) => Promise<void>
  updateCollection: (id: number, collectionId: number | null) => Promise<boolean>
  
  // État des opérations en cours
  workingIds: Record<number, string>
  
  // Métriques
  statusCounts: Record<string, number>
  orphanCount: number
}

/**
 * Hook pour gérer les contenus (ex-médias)
 * Responsabilité unique : CRUD contenus + polling
 */
export function useContents(options: UseContentsOptions = {}): UseContentsReturn {
  const { 
    collectionId = null, 
    status = 'all',
    enablePolling = true,
    pollingInterval = 2000,
  } = options

  // On charge TOUS les contenus pour avoir les compteurs corrects
  const [allContents, setAllContents] = useState<Content[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [workingIds, setWorkingIds] = useState<Record<number, string>>({})

  // Charger tous les contenus (sans filtre serveur)
  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/media')
      setAllContents(data.items || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement contenus')
    } finally {
      setLoading(false)
    }
  }, [])

  const remove = useCallback(async (id: number): Promise<boolean> => {
    setWorkingIds(w => ({ ...w, [id]: 'delete' }))
    try {
      await api.delete(`/media/${id}`)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur suppression contenu')
      return false
    } finally {
      setWorkingIds(w => {
        const { [id]: _, ...rest } = w
        return rest
      })
    }
  }, [reload])

  const reindex = useCallback(async (id: number, strategy: string = 'naive'): Promise<boolean> => {
    setWorkingIds(w => ({ ...w, [id]: strategy === 'llm_segmentation' ? 'llm_seg' : 'reindex' }))
    try {
      const { data } = await api.post(`/media/${id}/reindex`, null, { params: { strategy } })
      // Explicitly set chunking_strategy via dedicated endpoint (raw SQL, no ORM cache)
      await api.patch(`/media/${id}/chunking-strategy`, null, { params: { strategy } }).catch(() => {})
      if (data?.new_chunks !== undefined) {
        const msg = strategy === 'llm_segmentation'
          ? `✅ Segmentation IA terminée : ${data.new_chunks} segments (avant : ${data.old_chunks})`
          : `✅ Ré-indexé : ${data.new_chunks} chunks`
        setError(null)
        toast.success(msg)
      }
      await reload()
      return true
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Erreur ré-indexation'
      setError(detail)
      toast.error(detail)
      return false
    } finally {
      setWorkingIds(w => {
        const { [id]: _, ...rest } = w
        return rest
      })
    }
  }, [reload])

  const forceTranscribe = useCallback(async (id: number): Promise<boolean> => {
    setWorkingIds(w => ({ ...w, [id]: 'transcribe' }))
    try {
      await api.post(`/media/${id}/transcribe`)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur transcription')
      return false
    } finally {
      setWorkingIds(w => {
        const { [id]: _, ...rest } = w
        return rest
      })
    }
  }, [reload])

  const getDetail = useCallback(async (id: number): Promise<ContentDetail | null> => {
    try {
      const { data } = await api.get(`/media/${id}`)
      return data as ContentDetail
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement détail')
      return null
    }
  }, [])

  const downloadTranscription = useCallback(async (id: number): Promise<void> => {
    try {
      const res = await api.get(`/media/${id}/transcription.txt`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `media-${id}-transcription.txt`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      if (e?.response?.data instanceof Blob) {
        const txt = await e.response.data.text()
        try {
          const j = JSON.parse(txt)
          setError(j?.detail || 'Téléchargement impossible')
        } catch {
          setError(txt || 'Téléchargement impossible')
        }
      } else {
        setError(e?.response?.data?.detail || 'Téléchargement impossible')
      }
    }
  }, [])

  const updateCollection = useCallback(async (id: number, collectionId: number | null): Promise<boolean> => {
    setWorkingIds(w => ({ ...w, [id]: 'move' }))
    try {
      await api.patch(`/media/${id}/collection`, null, {
        params: { collection_id: collectionId }
      })
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur changement collection')
      return false
    } finally {
      setWorkingIds(w => {
        const { [id]: _, ...rest } = w
        return rest
      })
    }
  }, [reload])

  // Filtrage côté client pour l'affichage
  const filteredContents = useMemo(() => {
    let result = allContents

    // Filtre par collection
    if (collectionId === 'orphan') {
      result = result.filter((c: Content) => !c.subject_id)
    } else if (collectionId) {
      result = result.filter((c: Content) => c.subject_id === collectionId)
    }

    // Filtre par statut
    if (status !== 'all') {
      result = result.filter((c: Content) => c.status === status)
    }

    return result
  }, [allContents, collectionId, status])

  // Compteurs par statut (sur TOUS les contenus)
  const statusCounts = useMemo(() => {
    return allContents.reduce<Record<string, number>>((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1
      return acc
    }, {})
  }, [allContents])

  // Nombre d'orphelins (sur TOUS les contenus)
  const orphanCount = useMemo(() => {
    return allContents.filter((c: Content) => !c.subject_id).length
  }, [allContents])

  // Chargement initial
  useEffect(() => {
    reload()
  }, [reload])

  // Polling pour les contenus en traitement
  useEffect(() => {
    if (!enablePolling) return

    const processingItems = allContents.filter((m: Content) => 
      m.status === 'uploaded' || m.status === 'transcribing' || m.status === 'indexing'
    )
    
    if (processingItems.length === 0) return
    
    const interval = setInterval(async () => {
      try {
        for (const item of processingItems) {
          const { data } = await api.get(`/upload/status/${item.id}`)
          if (data.status !== item.status) {
            reload()
            break
          }
        }
      } catch (e) {
        console.error('Error polling content status:', e)
      }
    }, pollingInterval)
    
    return () => clearInterval(interval)
  }, [allContents, enablePolling, pollingInterval, reload])

  return {
    contents: filteredContents,
    allContents,
    loading,
    error,
    reload,
    remove,
    reindex,
    forceTranscribe,
    getDetail,
    downloadTranscription,
    updateCollection,
    workingIds,
    statusCounts,
    orphanCount,
  }
}

export default useContents
